import React, { useEffect, useState } from 'react'
import { Tab, makeStyles, tokens, Text, Button, SelectTabData } from '@fluentui/react-components'
import {
   InfoShield20Regular,
   SearchShield20Regular,
   TaskListSquareLtr20Regular,
   Warning20Regular,
   ClipboardTextLtr20Regular,
   ClipboardPulse20Regular,
   Warning20Filled,
   SearchShield20Filled,
   ClipboardPulse20Filled,
   ClipboardTextLtr20Filled,
   TaskListSquareLtr20Filled,
   InfoShield20Filled,
   LockClosed20Regular,
   LockClosed24Regular,
} from '@fluentui/react-icons'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ThreatScenario } from './ThreatScenario'
import { ConsequenceRiskAnalyse } from './ConsequenceRiskAnalyse'
import { ConsequenceEvaluation } from './ConsequenceEvaluation'
import { t } from 'i18next'
import { RiskAnalysis, ActionPlan, ActionPlanStatus, RiskAnalysisStatus } from '../../../../api/schemas/schema'
import { exportConsequenceAndProbabilityRiskActionPlansToExcel } from '../../../../helpers/excelHelper'
import {
   findConsequenceRiskParentToActionPlan,
   ConsequenceAndProbabilityAnalysisTabs,
   getConsequenceAndProbabilityRiskActionPlanColumns,
   getConsequenceAndProbabilityRiskActionPlanGroups,
   getConsequenceAndProbabilityRiskActionPlanSummaryFields,
} from '../../../../helpers/riskAnalysisHelper'
import { formatToSwedishCrowns } from '../../../../helpers/stringHelper'
import { IItemState } from '../../../../interfaces/IItemState'
import { NavigationLink } from '../../../../interfaces/INavigationLink'
import { Layout } from '../../../../pages/Layout'
import { ConsequenceAndProbabilityNavLinks } from '../../../common/navigation/ConsequenceAndProbabilityNavLinks'
import { BadgeActionPlanStatus } from '../../../demandAnalysis/BadgeActionPlanStatus'
import { ModalCompleteRiskAssessment } from '../../ModalCompleteRiskAssessment'
import { RiskActionPlan } from '../RiskActionPlan'
import { RiskIdentifying } from '../RiskIdentifying'
import { RiskInformation } from '../../RiskInformation'
import { ParentConsequencedRiskInformation } from './ParentConsequenceRiskInformation'

const useStyles = makeStyles({
   header: {
      display: 'flex',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
   },
   flexOnlyLarge: {
      '@media screen and (max-width: 1007px)': { display: 'none' },
   },
   spaceBtw: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
   },
   ellipsis: {
      '> * ': {
         display: '-webkit-box',
         WebkitBoxOrient: 'vertical',
         overflow: 'hidden',
         textOverflow: 'ellipsis',
         whiteSpace: 'normal',
         WebkitLineClamp: 3,
      },
   },
   phoneItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
   },
})

type ConsequenceAndProbabilityProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysisState: IItemState<RiskAnalysis>
   analysis: RiskAnalysis
}

export const ConsequenceAndProbabilityAnalysis = ({ setRiskAnalysisState, analysisState, analysis }: ConsequenceAndProbabilityProps) => {
   const classes = useStyles()
   const navigate = useNavigate()
   const [searchParams, setSearchParams] = useSearchParams()
   const [selectedItem, setSelectedItem] = useState<ActionPlan>(null)
   const [completeModalOpen, setCompleteModalOpen] = useState<boolean>(false)
   const selectedTab = searchParams.get('tab')

   useEffect(() => {
      if (searchParams.get('tab') === null) {
         setSearchParams((prev) => ({ ...prev, tab: 'info' }))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [searchParams])

   const consequenceRiskPhoneBody = (item: ActionPlan) => {
      const risk = findConsequenceRiskParentToActionPlan(analysis.consequenceAndProbabilityRisks, item)

      return (
         <div className={classes.phoneItem}>
            <span className={classes.spaceBtw}>
               <Text size={400}>{risk.scenario}</Text>
               <BadgeActionPlanStatus status={item.status} />
            </span>
            <Text size={300}>{risk.estimatedCost || risk.estimatedCost === 0 ? formatToSwedishCrowns(risk.estimatedCost) + ' kr' : null}</Text>
            <Text size={300} className={classes.ellipsis}>
               {risk.consequence}
            </Text>
         </div>
      )
   }

   const completeBtnDisabled =
      analysis.consequenceAndProbabilityRisks.length === 0 ||
      (analysis.actionPlans.length === 0 && analysis.consequenceAndProbabilityRisks.some((r) => r.analysedById == null)) ||
      analysis.actionPlans.some((ap) => ap.status === ActionPlanStatus.NotDecided || analysis.status === RiskAnalysisStatus.Completed)

   const getConsequenceAndProbabilityTitle = (tab: string): string => {
      switch (tab) {
         case ConsequenceAndProbabilityAnalysisTabs.Info:
            return t('Information')
         case ConsequenceAndProbabilityAnalysisTabs.ThreatScenario:
            return t('ThreatScenario')
         case ConsequenceAndProbabilityAnalysisTabs.Identifying:
            return t('RiskIdentifying')
         case ConsequenceAndProbabilityAnalysisTabs.Analysis:
            return t('RiskAnalysis')
         case ConsequenceAndProbabilityAnalysisTabs.Evaluation:
            return t('RiskEvaluation')
         case ConsequenceAndProbabilityAnalysisTabs.ActionPlan:
            return t('RiskManagementPlan')
      }
   }

   return (
      <Layout
         activeDesktopLink={NavigationLink.RiskManagement}
         phoneNavLinks={
            <ConsequenceAndProbabilityNavLinks
               id={analysis.id}
               active={selectedTab}
               actionTab={<Tab value={'complete'} icon={<LockClosed24Regular />} disabled={completeBtnDisabled} />}
            />
         }
         customOnTabSelect={(e, data: SelectTabData) => {
            if (data.value === 'complete') setCompleteModalOpen(true)
            else navigate(data.value)
         }}
         breadCrumbItems={[
            { path: '/risk-management', active: false, title: t('RiskManagement') },
            { path: `/risk-management/${analysis.id}`, active: true, title: analysis.name },
         ]}
         tabs={
            <>
               <Tab
                  icon={selectedTab === ConsequenceAndProbabilityAnalysisTabs.Info ? <InfoShield20Filled /> : <InfoShield20Regular />}
                  value={ConsequenceAndProbabilityAnalysisTabs.Info}
               >
                  {getConsequenceAndProbabilityTitle(ConsequenceAndProbabilityAnalysisTabs.Info)}
               </Tab>
               <Tab
                  icon={selectedTab === ConsequenceAndProbabilityAnalysisTabs.ThreatScenario ? <Warning20Filled /> : <Warning20Regular />}
                  value={ConsequenceAndProbabilityAnalysisTabs.ThreatScenario}
               >
                  {getConsequenceAndProbabilityTitle(ConsequenceAndProbabilityAnalysisTabs.ThreatScenario)}
               </Tab>
               <Tab
                  icon={selectedTab === ConsequenceAndProbabilityAnalysisTabs.Identifying ? <SearchShield20Filled /> : <SearchShield20Regular />}
                  value={ConsequenceAndProbabilityAnalysisTabs.Identifying}
               >
                  {getConsequenceAndProbabilityTitle(ConsequenceAndProbabilityAnalysisTabs.Identifying)}
               </Tab>
               <Tab
                  icon={selectedTab === ConsequenceAndProbabilityAnalysisTabs.Analysis ? <ClipboardTextLtr20Filled /> : <ClipboardTextLtr20Regular />}
                  value={ConsequenceAndProbabilityAnalysisTabs.Analysis}
               >
                  {getConsequenceAndProbabilityTitle(ConsequenceAndProbabilityAnalysisTabs.Analysis)}
               </Tab>
               <Tab
                  icon={selectedTab === ConsequenceAndProbabilityAnalysisTabs.Evaluation ? <ClipboardPulse20Filled /> : <ClipboardPulse20Regular />}
                  value={ConsequenceAndProbabilityAnalysisTabs.Evaluation}
               >
                  {getConsequenceAndProbabilityTitle(ConsequenceAndProbabilityAnalysisTabs.Evaluation)}
               </Tab>
               <Tab
                  icon={selectedTab === ConsequenceAndProbabilityAnalysisTabs.ActionPlan ? <TaskListSquareLtr20Filled /> : <TaskListSquareLtr20Regular />}
                  value={ConsequenceAndProbabilityAnalysisTabs.ActionPlan}
               >
                  {getConsequenceAndProbabilityTitle(ConsequenceAndProbabilityAnalysisTabs.ActionPlan)}
               </Tab>
            </>
         }
         button={
            <Button disabled={completeBtnDisabled} icon={<LockClosed20Regular />} onClick={() => setCompleteModalOpen(true)} appearance="transparent">
               {t('CompleteRiskAssessment')}
            </Button>
         }
         overflowAuto={true}
      >
         {selectedTab === ConsequenceAndProbabilityAnalysisTabs.Info && (
            <RiskInformation analysisState={analysisState} setAnalysisState={setRiskAnalysisState} />
         )}
         {selectedTab === ConsequenceAndProbabilityAnalysisTabs.ThreatScenario && (
            <ThreatScenario analysisState={analysisState} setAnalysisState={setRiskAnalysisState} />
         )}

         {selectedTab === ConsequenceAndProbabilityAnalysisTabs.Identifying && (
            <RiskIdentifying analysisState={analysisState} setRiskAnalysisState={setRiskAnalysisState} />
         )}
         {selectedTab === ConsequenceAndProbabilityAnalysisTabs.Analysis && (
            <ConsequenceRiskAnalyse analysisState={analysisState} setRiskAnalysisState={setRiskAnalysisState} />
         )}
         {selectedTab === ConsequenceAndProbabilityAnalysisTabs.Evaluation && (
            <ConsequenceEvaluation analysisState={analysisState} setRiskAnalysisState={setRiskAnalysisState} />
         )}
         {selectedTab === ConsequenceAndProbabilityAnalysisTabs.ActionPlan && (
            <RiskActionPlan<ActionPlan>
               actionPlans={analysis.actionPlans}
               title={t('RiskManagementPlan')}
               analysisState={analysisState}
               setRiskAnalysisState={setRiskAnalysisState}
               getItemColumns={getConsequenceAndProbabilityRiskActionPlanColumns(analysis.consequenceAndProbabilityRisks)}
               getItemGroups={getConsequenceAndProbabilityRiskActionPlanGroups(analysis.consequenceAndProbabilityRisks, analysis.actionPlans)}
               getItemSummaryFields={getConsequenceAndProbabilityRiskActionPlanSummaryFields(analysis.actionPlans, analysis.consequenceAndProbabilityRisks)}
               parentRiskInformation={
                  <ParentConsequencedRiskInformation selectedRisk={analysis.consequenceAndProbabilityRisks.find((r) => r.id === selectedItem?.riskId)} />
               }
               getPhoneBody={consequenceRiskPhoneBody}
               selectedItem={selectedItem}
               setSelectedItem={setSelectedItem}
               exportToExcel={(analysis, actionPlans) => exportConsequenceAndProbabilityRiskActionPlansToExcel(analysis, actionPlans)}
            />
         )}

         <ModalCompleteRiskAssessment
            open={completeModalOpen}
            close={() => setCompleteModalOpen(false)}
            riskAnalysisState={analysisState}
            setRiskAnalysisState={setRiskAnalysisState}
         />
      </Layout>
   )
}
